// M2R.5 — EmployeePicker: NV chọn tên mình trên máy kiosk.
// Dùng inline trên HomePage khi user=kiosk + chưa có L2 session.
//
// Props:
//   - onPick(employee, kind): kind ∈ 'setup' | 'verify'
//     'setup'  → NV chưa enable L2 → parent mở L2SetupModal
//     'verify' → NV đã enable L2  → parent mở L2OtpModal
//
// Backend trả list từ GET /auth/l2/employees (shop của máy, status=active).
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { api } from '../lib/api.js';

export default function EmployeePicker({ onPick }) {
  const [list, setList] = useState(null);
  const [err, setErr] = useState(null);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.l2Employees();
        if (!cancelled) setList(r.employees || []);
      } catch (e) {
        if (!cancelled) setErr(e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  function click(emp) {
    const lockedUntil = emp.locked_until ? new Date(emp.locked_until) : null;
    if (lockedUntil && lockedUntil > new Date()) {
      toast.error(`NV đang bị khoá đến ${lockedUntil.toLocaleTimeString('vi-VN')} — báo admin`);
      return;
    }
    if (!emp.otp_l2_enabled) {
      onPick?.(emp, 'setup');
    } else {
      onPick?.(emp, 'verify');
    }
  }

  if (err) {
    return (
      <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 text-sm text-rose-700">
        Không lấy được danh sách NV: {err.data?.error || err.message}
      </div>
    );
  }

  if (list === null) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 p-6 text-center text-slate-500">
        Đang tải danh sách NV…
      </div>
    );
  }

  if (list.length === 0) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 text-center">
        <div className="text-4xl mb-2">👥</div>
        <div className="font-semibold text-amber-800">Shop này chưa có NV nào</div>
        <div className="text-sm text-amber-700 mt-1">
          Báo admin thêm NV ở trang <span className="font-mono">/admin/employees</span>
        </div>
      </div>
    );
  }

  const q = filter.trim().toLowerCase();
  const filtered = q
    ? list.filter(e =>
        e.full_name.toLowerCase().includes(q) ||
        (e.phone || '').includes(q)
      )
    : list;

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <h3 className="text-lg font-semibold text-slate-800">👋 Chào bạn! Chọn tên để bắt đầu</h3>
          <p className="text-xs text-slate-500">{list.length} NV trong shop — bấm vào tên bạn</p>
        </div>
        {list.length > 6 && (
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Tìm tên / SĐT…"
            className="px-3 py-1.5 rounded-lg border border-slate-300 text-sm w-48"
          />
        )}
      </div>

      {/* M78b (2026-04-25): vertical card layout — avatar to, tên đầy đủ wrap 2 dòng,
          status pill nhỏ gọn. Trước đây horizontal + truncate cắt tên dài
          ("Vũ Phạm Thùy..." → giờ hiển thị đủ "Vũ Phạm Thùy Dương"). */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
        {filtered.map((e) => {
          const locked = e.locked_until && new Date(e.locked_until) > new Date();
          return (
            <button
              key={e.id}
              data-testid={`emp-pick-${e.id}`}
              onClick={() => click(e)}
              disabled={locked}
              className={`
                relative rounded-2xl border p-4 text-center transition shadow-sm flex flex-col items-center gap-2 min-h-[170px]
                ${locked
                  ? 'bg-slate-50 border-slate-200 opacity-60 cursor-not-allowed'
                  : e.otp_l2_enabled
                    ? 'bg-white hover:bg-brand-50 hover:border-brand-300 hover:shadow-md border-slate-200'
                    : 'bg-amber-50 hover:bg-amber-100 hover:border-amber-300 hover:shadow-md border-amber-200'
                }
              `}
            >
              {/* Avatar lớn ở giữa */}
              <div className={`w-14 h-14 rounded-full flex items-center justify-center font-bold text-white text-lg shrink-0
                ${e.otp_l2_enabled ? 'bg-brand-500' : 'bg-amber-500'}`}>
                {initials(e.full_name)}
              </div>

              {/* Tên đầy đủ — wrap 2 dòng, max 3 dòng cho tên rất dài */}
              <div className="w-full flex-1 flex flex-col justify-center">
                <div className="font-semibold text-slate-800 text-sm leading-snug break-words line-clamp-3">
                  {e.full_name}
                </div>
                {e.phone && (
                  <div className="text-[11px] text-slate-500 mt-0.5 truncate">{e.phone}</div>
                )}
              </div>

              {/* Status pill nhỏ ở đáy */}
              <div className="text-[10px] font-medium w-full">
                {locked
                  ? <span className="inline-block px-2 py-0.5 rounded-full bg-rose-100 text-rose-700">🔒 Đang khoá</span>
                  : e.otp_l2_enabled
                    ? <span className="inline-block px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">✓ Sẵn sàng</span>
                    : <span className="inline-block px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">⚠️ Cần thiết lập TOTP</span>
                }
              </div>
            </button>
          );
        })}
        {filtered.length === 0 && (
          <div className="col-span-full text-center text-sm text-slate-500 py-4">
            Không tìm thấy NV phù hợp
          </div>
        )}
      </div>
    </div>
  );
}

function initials(name) {
  const parts = (name || '').trim().split(/\s+/);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0][0]?.toUpperCase() || '?';
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
